/**
 * TypeScript mirrors of the variant-lens-api /cases/{id}/variants response.
 *
 * Kept intentionally permissive: `info` and the per-sample FORMAT maps are raw
 * VCF passthrough, so their keys depend on which annotation pipeline produced
 * the VCF. The UI reads known keys defensively and treats everything else as
 * opaque (surfaced only in the raw INFO/FORMAT collapsible).
 */

export type SampleData = {
  GT?: Array<number | null>
  GQ?: number | null
  DP?: number | null
  AD?: number[]
  VAF?: number[]
  [key: string]: unknown
}

/**
 * Computed depth summary for a CNV record (GEN-4383). Derived server-side from
 * the caller's normalized depth stats already in the VCF (FORMAT/DPS[0],
 * DP0S[0]); gives the reviewer the CNV Manual Review SOP depth gate as a number
 * instead of eyeballing the coverage histogram. Null for non-CNV records.
 */
export type CnvDepth = {
  /** Normalized depth ratio vs the diploid baseline (DPS[0]). */
  ratio: number
  /** Same ratio from non-zero-MQ reads (DP0S[0]); backs the mappability flag. */
  ratio_mapq0: number | null
  /** Lower bound of the expected band for the called CN. */
  expected_min: number
  /** Upper bound of the expected band; null means unbounded (CN>=4). */
  expected_max: number | null
  /** True when ratio falls in the expected band for the called CN. */
  in_expected_band: boolean
  /** Human-readable expectation for the called CN, e.g. "heterozygous deletion (~0.5x)". */
  band_label: string
  /**
   * True when all-reads depth is elevated but the non-zero-MQ depth collapses:
   * the paralog/segdup fingerprint the SOP flags for gains. Cross-check with
   * the segmental-dups track.
   */
  mappability_flag: boolean
}

/**
 * VAF-mode summary for a CNV gain interval (GEN-4646). Computed server-side from
 * the sample's SNV track over the CNV's chrom:pos-end: a gain shifts germline het
 * VAFs to off-center modes (CN=3 -> ~1/3, ~2/3; CN>=4 -> ~1/4, ~3/4) rather than
 * the diploid ~0.5. Reports how usable het VAFs distribute across the CN-expected
 * modes vs the diploid center (shares, not a verdict: the signal is noisy and
 * diploid VAFs are reference-biased). Present only for gains; null otherwise.
 */
export type CnvVafMode = {
  /** Called copy number the expected modes are derived from (INFO/CN). */
  cn: number
  /** Expected off-center het-VAF modes for this CN (e.g. [0.333, 0.667]). */
  expected_modes: number[]
  /** Heterozygous sites with a usable VAF in the interval. */
  n_het_vaf: number
  /** Het VAFs within tolerance of an expected off-center mode. */
  n_at_expected_modes: number
  /** Het VAFs within tolerance of the diploid ~0.5 mode. */
  n_diploid: number
  /** n_at_expected_modes / n_het_vaf; null when no usable het VAF. */
  frac_at_expected_modes: number | null
  /** n_diploid / n_het_vaf; null when no usable het VAF. */
  frac_diploid: number | null
}

/**
 * Loss-of-heterozygosity summary for a CNV interval (GEN-4448). Computed
 * server-side from the sample's SNV track over the CNV's chrom:pos-end: het and
 * hom-alt counts, balanced-het count (alt fraction in 0.30-0.70), and het_frac.
 * het_frac trends to 1.0 in a balanced diploid region and toward 0.0 under a
 * hemizygous loss. Null for non-CNV records or when the SNV track is absent.
 */
export type CnvReviewMetrics = {
  /** Fully-called SNV sites scanned in the interval. */
  n_snv: number
  /** Heterozygous sites (two distinct called alleles). */
  n_het: number
  /** Homozygous-alt sites (both alleles the same non-ref index). */
  n_hom_alt: number
  /** Heterozygous sites whose alt fraction sits in the balanced 0.30-0.70 band. */
  n_balanced_het: number
  /** n_het / (n_het + n_hom_alt); null when the interval has no alt-bearing site. */
  het_frac: number | null
  /**
   * VAF-mode summary for gains (cn >= 3), from the same SNV scan (GEN-4646).
   * Null for losses/diploid or when the interval has no usable het VAF.
   */
  vaf_mode?: CnvVafMode | null
}

export type Variant = {
  variant_id: string
  chrom: string
  pos: number
  rsid: string | null
  ref: string
  alt: string[]
  qual: number | null
  filter: string[]
  /** Inclusive 1-based end of the reference span; equals pos for SNVs, INFO/END for structural records. */
  end?: number
  /** INFO/SVTYPE for structural records (DEL, DUP, CNV); null/absent for SNVs. */
  svtype?: string | null
  /** INFO/SVLEN (signed) for structural records; null/absent for SNVs. */
  svlen?: number | null
  /** INFO/CN copy number for CNV records; null/absent otherwise. */
  cn?: number | null
  /** Computed depth summary for CNV records; null/absent for non-CNV records (GEN-4383). */
  depth?: CnvDepth | null
  /**
   * Single-variant lookup only: LOH summary over the CNV interval from the
   * sample's SNV track (GEN-4448). Null/absent for non-CNV records, list/facets
   * responses, or when the SNV track is unavailable.
   */
  review_metrics?: CnvReviewMetrics | null
  info: Record<string, unknown>
  samples: Record<string, SampleData>
  /**
   * Single-variant lookup only: the track_id whose VCF the locus matched.
   * Lets the UI drop a stale ?variant= deep link that resolved on a different
   * track than the selected one (GEN-4350). Absent on list/facets responses.
   */
  matched_track?: string | null
}

export type PaginatedVariantsResponse = {
  case_id: string
  page_size: number
  variants: Variant[]
  next_cursor: string | null
}

/** One selectable variant VCF for a case/sample (e.g. small-variant vs CNV). */
export type VariantTrack = {
  track_id: string
  label: string
  /** True for the track the API queries when ?track= is omitted. */
  default: boolean
}

export type VariantTracksResponse = {
  case_id: string
  tracks: VariantTrack[]
}

/** Render a variant_id (chr1-100-A-G) as an IGV-style locus (chr1:100). */
export function variantLocus(v: Pick<Variant, 'chrom' | 'pos'>): string {
  return `${v.chrom}:${v.pos}`
}

/**
 * True when a record carries the CNV depth block. Using `depth` (rather than
 * guessing from svtype) matches exactly the records the API computed depth for
 * (INFO/CN present), so it is the reliable "this is a CNV to review" signal.
 */
export function isCnv(v: Pick<Variant, 'depth'>): boolean {
  return v.depth != null
}

/**
 * Region for the selected-variant highlight box in IGV. CNVs box the full
 * interval (chrom:pos-end) so the reviewer sees the whole call; other records
 * fall back to the single position (chrom:pos). GEN-4383.
 */
export function variantRoiLocus(v: Pick<Variant, 'chrom' | 'pos' | 'end' | 'depth'>): string {
  if (isCnv(v) && typeof v.end === 'number' && v.end > v.pos) {
    return `${v.chrom}:${v.pos}-${v.end}`
  }
  return variantLocus(v)
}

/** First genotype-bearing sample name, used as the "proband" proxy in v1. */
export function probandSampleName(v: Variant): string | null {
  const names = Object.keys(v.samples)
  return names.length > 0 ? names[0] : null
}

/**
 * het_frac at or below this level reads as loss of heterozygosity: germline
 * hets have collapsed to apparent homozygous, the SOP signal that supports a
 * true hemizygous loss. Matches the observed CN=1 example (het_frac ~0.10) while
 * staying clear of balanced diploid regions (het_frac near 1.0). GEN-4448.
 */
export const LOH_HET_FRAC_THRESHOLD = 0.15

export type LohInterpretation = {
  /** True when het_frac is at/below the LOH threshold. */
  loh: boolean
  /** Short badge label for the reviewer. */
  label: string
  /** Tailwind badge classes matching the verdict. */
  badgeClass: string
}

/**
 * Interpret a CNV interval's het fraction for the reviewer. Returns null when
 * het_frac is null (no alt-bearing sites to judge), so the caller shows the raw
 * counts without a verdict badge.
 */
export function lohInterpretation(m: Pick<CnvReviewMetrics, 'het_frac'>): LohInterpretation | null {
  if (m.het_frac === null) return null
  const loh = m.het_frac <= LOH_HET_FRAC_THRESHOLD
  return loh
    ? { loh: true, label: 'LOH: supports loss', badgeClass: 'bg-amber-100 text-amber-800' }
    : { loh: false, label: 'heterozygosity retained', badgeClass: 'bg-green-100 text-green-800' }
}

/**
 * Render an expected VAF mode as a readable fraction ("1/3", "2/3", "1/4",
 * "3/4"), matching the SOP's language. Falls back to a 2-dp decimal for any mode
 * that isn't one of the known gain fractions.
 */
export function formatVafMode(mode: number): string {
  const known: Array<[number, string]> = [
    [1 / 4, '1/4'],
    [1 / 3, '1/3'],
    [1 / 2, '1/2'],
    [2 / 3, '2/3'],
    [3 / 4, '3/4'],
  ]
  for (const [value, label] of known) {
    if (Math.abs(mode - value) < 0.01) return label
  }
  return mode.toFixed(2)
}

/** Format a genotype array (e.g. [0,1]) as a VCF GT string (e.g. 0/1). */
export function formatGT(gt: Array<number | null> | undefined): string {
  if (!gt || gt.length === 0) return './.'
  return gt.map((a) => (a === null ? '.' : String(a))).join('/')
}

/**
 * LSMC variant-confidence categories carried in INFO/TAG, ordered best -> worst.
 * Drives the TAG facet ordering and the `tag` sort key. Matches the values the
 * hybrid annotation pipeline emits (see GEN-3101 / GEN-2887).
 */
export const TAG_CATEGORIES = [
  'Confirmed',
  'NeedsReview-AlleleBalance',
  'NeedsReview-HighSB',
  'NeedsReview-LowCoverage',
  'NeedsReview-VariantCluster',
  'NeedsReview-LongReadOnly',
  'NeedsReview-Unconfirmed',
  'NeedsReview-Other',
  'ModelRejected',
] as const

/**
 * Human-readable definition of each LSMC confidence tag, shown in the TAG
 * filter legend. Provisional: the tagging rules are expected to change, so this
 * is hardcoded here (like the column-help text) for easy editing in one place.
 */
export const TAG_DEFINITIONS: Record<(typeof TAG_CATEGORIES)[number], string> = {
  Confirmed:
    'Assigned if none of the other conditions are true and both long-read (LAD) and short-read (SAD) support exist for the alternate allele.',
  'NeedsReview-AlleleBalance': 'Assigned to heterozygous calls where allele balance is outside 0.3 to 0.7.',
  'NeedsReview-HighSB': 'Assigned if the INFO SOR is greater than 2.5.',
  'NeedsReview-LowCoverage': 'Assigned if the total depth (DP) is under 20.',
  'NeedsReview-VariantCluster': 'Assigned to variants in a dense cluster (5 or more variants within a 10 bp window).',
  'NeedsReview-LongReadOnly': 'Assigned if the alternate allele only has long-read (LAD) support.',
  'NeedsReview-Unconfirmed': 'Assigned if the variant only has short-read (SAD) support.',
  'NeedsReview-Other': 'Default tag if none of the other conditions are met.',
  ModelRejected: 'Assigned if the FILTER status is MLrejected.',
}

/**
 * INFO keys that may carry the review-confidence category, in precedence order.
 * The original LSMC pipeline used `TAG`; the updated SeqOne DNAscope-Hybrid
 * delivery renamed it to `IFX_REVIEW_TAG` (same vocabulary). We read both so
 * either schema - or a rollback to the old name - works; `TAG` wins when both
 * are present. Mirrors the API's TAG_INFO_KEYS (GEN-4220 / GEN-4216).
 */
export const TAG_INFO_KEYS = ['TAG', 'IFX_REVIEW_TAG'] as const

/** Review category for a variant, or null when the VCF carries no tag. */
export function variantTag(v: Pick<Variant, 'info'>): string | null {
  for (const key of TAG_INFO_KEYS) {
    const raw = v.info?.[key]
    const value = Array.isArray(raw) ? raw[0] : raw
    if (typeof value === 'string' && value.length > 0) return value
  }
  return null
}

/**
 * Read a numeric INFO field. VCF Number=A fields arrive as arrays (one per ALT);
 * we take the first allele. Returns null when absent or non-finite.
 */
export function infoNumber(v: Pick<Variant, 'info'>, key: string): number | null {
  const raw = v.info?.[key]
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
  if (Array.isArray(raw) && typeof raw[0] === 'number') return Number.isFinite(raw[0]) ? raw[0] : null
  return null
}

/** True when an INFO flag field (e.g. STR) is set. */
export function infoFlag(v: Pick<Variant, 'info'>, key: string): boolean {
  const raw = v.info?.[key]
  return raw === true || raw === 1
}

/**
 * Variant allele fraction for a sample: prefer FORMAT/VAF, else derive from AD
 * (alt depth / total depth). The hybrid VCF has no VAF field, so the AD fallback
 * is what populates the VAF column/cards there.
 */
export function sampleVaf(sample: SampleData | undefined): number | null {
  if (!sample) return null
  if (sample.VAF && sample.VAF.length > 0 && typeof sample.VAF[0] === 'number') return sample.VAF[0]
  const ad = sample.AD
  if (ad && ad.length >= 2) {
    const total = ad.reduce((s, x) => s + (x ?? 0), 0)
    if (total > 0) return (ad[1] ?? 0) / total
  }
  return null
}

/** VAF from a VCF allele-depth array [ref, alt, ...]: alt / total, or null. */
function vafFromDepths(ad: unknown): number | null {
  if (!Array.isArray(ad) || ad.length < 2) return null
  const nums = ad.map((x) => (typeof x === 'number' && Number.isFinite(x) ? x : 0))
  const total = nums.reduce((s, x) => s + x, 0)
  if (total <= 0) return null
  return (nums[1] ?? 0) / total
}

/** Short-read VAF for a sample from FORMAT/SAD (alt depth / total), or null. */
export function srVaf(sample: SampleData | undefined): number | null {
  return sample ? vafFromDepths(sample.SAD) : null
}

/** Long-read VAF for a sample from FORMAT/LAD (alt depth / total), or null. */
export function lrVaf(sample: SampleData | undefined): number | null {
  return sample ? vafFromDepths(sample.LAD) : null
}

/** Tailwind classes for a TAG badge, color-coded by confidence category. */
export function tagBadgeClass(tag: string): string {
  if (tag === 'Confirmed') return 'bg-green-100 text-green-800'
  if (tag === 'ModelRejected') return 'bg-red-100 text-red-800'
  if (tag.startsWith('NeedsReview')) return 'bg-amber-100 text-amber-800'
  return 'bg-gray-100 text-gray-700'
}

/** Compact badge label: drop the "NeedsReview-" prefix to keep rows dense. */
export function tagShortLabel(tag: string): string {
  return tag.startsWith('NeedsReview-') ? tag.slice('NeedsReview-'.length) : tag
}
